import { useEffect } from "react";

const PRINT_MODE_CLASS = "zr-printing-badges";
const SOLO_MODE_CLASS = "zr-printing-badges--solo";

/**
 * Put the document into badge-print mode while a badge surface is on screen.
 *
 * Every `@media print` rule in badge-sheet.css is gated on these body classes,
 * so printing any other page of the portal behaves exactly as it did before.
 * The class is held for as long as the surface is mounted rather than only
 * around our own `window.print()` call, so a plain Ctrl+P prints the sheet too.
 *
 * `solo` additionally hides the app shell behind a modal — the single-student
 * card is rendered inside one.
 */
export function useBadgePrintMode(active, solo = false) {
  useEffect(() => {
    if (!active) return undefined;
    const { body } = document;
    body.classList.add(PRINT_MODE_CLASS);
    if (solo) body.classList.add(SOLO_MODE_CLASS);
    return () => {
      body.classList.remove(PRINT_MODE_CLASS);
      body.classList.remove(SOLO_MODE_CLASS);
    };
  }, [active, solo]);
}

/**
 * Print exactly one card out of a sheet.
 *
 * `soloId` marks the card to keep; every other card picks up `badge-card--skip`
 * and drops out of the print stylesheet. We let that class change paint, open
 * the dialog, and put the sheet back however the dialog closes.
 */
export function useSoloPrint(soloId, onDone) {
  useEffect(() => {
    if (soloId === null || soloId === undefined) return undefined;
    const finish = () => onDone();
    window.addEventListener("afterprint", finish);
    const timer = window.setTimeout(() => {
      window.print(); // blocks until the dialog closes on desktop browsers
      finish();
    }, 50);
    return () => {
      window.removeEventListener("afterprint", finish);
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloId]);
}
